import { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Link,
  useLocation,
  useMatches,
  useNavigate,
  useSearchParams
} from 'react-router';
import { Select } from '@linktivity/link-ui';
import { EnvBadge, Locale, User } from '@link/share/components';
import { clearAutoLogoutStorage } from '@link/share/hooks';
import { linktivity } from '@link/share/icons';
import { useStore } from '@link/supplier/stores';
import supplierAuth from '@link/supplier/vendors/firebase';
import styles from './header.module.css';

const HeaderView: React.FC = () => {
  const { auth, supplier } = useStore();
  const matches = useMatches();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingSupplierRef = useRef<string | null>(null);

  // Apply pending supplier switch only after the URL has changed to the list
  // page, so the detail component is already unmounted and cannot fire requests
  // with the new supplier ID.
  useEffect(() => {
    if (pendingSupplierRef.current === null) return;
    const value = pendingSupplierRef.current;
    pendingSupplierRef.current = null;
    supplier.setSelectSupplierId(value);
    supplier.setSelectCustomerId(
      supplier.getSupplierCustomIdBySupplierId(value)
    );
  }, [location.pathname, supplier]);

  const logout = useCallback(async () => {
    clearAutoLogoutStorage();
    await supplierAuth.logout();
    supplier.clearSelectSupplierInfo();
  }, [supplier]);

  const showSelect = () => {
    return !matches.some(
      match =>
        (match.handle as { hideSupplierSelect?: boolean })
          ?.hideSupplierSelect === true
    );
  };

  const handleSupplierSelect = useCallback(
    (value: string) => {
      const detailParents = [
        '/contracts',
        '/book/booking',
        '/bulk-order/list',
        '/finance/transactions',
        '/allotment-settings'
      ];
      for (const parent of detailParents) {
        if (location.pathname.startsWith(parent + '/')) {
          pendingSupplierRef.current = value;
          navigate(parent, { replace: true });
          return;
        }
      }
      const customId = supplier.getSupplierCustomIdBySupplierId(value);
      supplier.setSelectSupplierId(value);
      supplier.setSelectCustomerId(customId);
      // 若当前 URL 带有 ?supplier= 参数，同步更新，避免页面重挂载后 apply effect 读到旧值再切回来
      if (searchParams.has('supplier')) {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            next.set('supplier', customId);
            return next;
          },
          { replace: true }
        );
      }
    },
    [location.pathname, navigate, supplier, searchParams, setSearchParams]
  );

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <div className={styles.brand}>
          <Link to="/" className={styles.logo}>
            <img src={linktivity} alt="logo" width="160" />
          </Link>
          <EnvBadge />
        </div>
        <div className={styles.right}>
          {auth.isLinkAdmin && showSelect() && (
            <Select
              options={supplier.supplierOpts}
              filterable
              value={supplier.selectSupplierId}
              onSelect={handleSupplierSelect}
              className={styles.input}
            />
          )}
          <Locale />
          <User email={auth.user?.email || ''} logout={logout} />
        </div>
      </div>
    </header>
  );
};
const Header = observer(HeaderView);
export default Header;
